import React from 'react';
import {
    FileText, FilePlus2, PenLine, LayoutTemplate, SquareCheck, Loader2, Square, AlertTriangle,
} from 'lucide-react';
import { cn } from '../../lib/utils';

// ═══════════════════════════════════════════════════════════════════════════════
// TOOL SKELETON CARD
//
// Placeholder for a tool the agent has announced but not finished applying.
//
// The reply arrives with its tool calls already decided, but each one then costs
// another round-trip — document edits cost one or two more model calls on top.
// This fills that gap with the one thing we genuinely know: which tool is coming
// and what it targets. Geometry matches EditedDocumentCard's loading branch
// (mt-3 p-3 rounded-xl, 40px leading tile) so the real card fills in rather than
// replacing this one.
// ═══════════════════════════════════════════════════════════════════════════════

const TOOL_META = {
    create_document: { label: 'Creating document', icon: FilePlus2, tone: 'primary' },
    rewrite_document: { label: 'Rewriting document', icon: PenLine, tone: 'orange' },
    insert_in_document: { label: 'Inserting content', icon: PenLine, tone: 'green' },
    replace_in_document: { label: 'Replacing content', icon: PenLine, tone: 'yellow' },
    create_mockup: { label: 'Building mockup', icon: LayoutTemplate, tone: 'accent' },
    rewrite_mockup: { label: 'Rewriting mockup', icon: LayoutTemplate, tone: 'orange' },
    insert_in_mockup: { label: 'Updating mockup', icon: LayoutTemplate, tone: 'green' },
    replace_in_mockup: { label: 'Updating mockup', icon: LayoutTemplate, tone: 'yellow' },
    create_tasks: { label: 'Creating tasks', icon: SquareCheck, tone: 'green' },
    update_tasks: { label: 'Updating tasks', icon: SquareCheck, tone: 'primary' },
    delete_tasks: { label: 'Removing tasks', icon: SquareCheck, tone: 'red' },
    modify_task: { label: 'Updating task', icon: SquareCheck, tone: 'primary' },
};

const TONES = {
    primary: { shell: 'from-primary/15 to-indigo-600/5 border-primary/25', tile: 'bg-primary/20 border-primary/30', fg: 'text-primary' },
    accent: { shell: 'from-accent/15 to-cyan-600/5 border-accent/25', tile: 'bg-accent/20 border-accent/30', fg: 'text-accent' },
    green: { shell: 'from-green-500/15 to-emerald-600/5 border-green-500/25', tile: 'bg-green-500/20 border-green-500/30', fg: 'text-green-400' },
    orange: { shell: 'from-orange-500/15 to-red-600/5 border-orange-500/25', tile: 'bg-orange-500/20 border-orange-500/30', fg: 'text-orange-400' },
    yellow: { shell: 'from-yellow-500/15 to-amber-600/5 border-yellow-500/25', tile: 'bg-yellow-500/20 border-yellow-500/30', fg: 'text-yellow-400' },
    red: { shell: 'from-red-500/15 to-rose-600/5 border-red-500/25', tile: 'bg-red-500/20 border-red-500/30', fg: 'text-red-400' },
};

export function ToolSkeletonCard({ toolName, target, status = 'running' }) {
    const meta = TOOL_META[toolName] || { label: 'Working', icon: FileText, tone: 'primary' };
    const tone = TONES[meta.tone] || TONES.primary;
    const Icon = meta.icon;

    const isTerminal = status === 'cancelled' || status === 'failed';
    const shell = isTerminal ? 'from-white/[0.04] to-white/[0.01] border-white/10' : tone.shell;
    const tile = isTerminal ? 'bg-white/5 border-white/10' : tone.tile;
    const fg = isTerminal ? 'text-muted-foreground' : tone.fg;

    let StatusIcon = Loader2;
    if (status === 'cancelled') StatusIcon = Square;
    else if (status === 'failed') StatusIcon = AlertTriangle;
    else if (status === 'queued') StatusIcon = Icon;

    return (
        <div
            className={cn('mt-3 rounded-xl border bg-gradient-to-r p-3 backdrop-blur-sm', shell)}
            data-testid={`tool-skeleton-${toolName}`}
        >
            <div className="flex items-center gap-3">
                <div className={cn('flex h-10 w-10 items-center justify-center rounded-xl border', tile)}>
                    <StatusIcon
                        className={cn('h-5 w-5', fg, status === 'running' && 'animate-spin')}
                    />
                </div>
                <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-2 truncate text-sm font-semibold">
                        {target || meta.label}
                        <span
                            className={cn(
                                'rounded-md border px-1.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-[0.14em]',
                                tile, fg,
                                status === 'running' && 'animate-pulse'
                            )}
                        >
                            {status === 'cancelled' ? 'Stopped' : status === 'failed' ? 'Failed' : status === 'queued' ? 'Queued' : 'Working'}
                        </span>
                    </p>
                    {status === 'running' ? (
                        <div className="mt-1.5 h-1.5 w-2/3 overflow-hidden rounded-full bg-white/5">
                            <span className="block h-full w-1/3 animate-[shimmer_1.2s_ease-in-out_infinite] rounded-full bg-primary/50" />
                        </div>
                    ) : (
                        <p className={cn('text-xs', fg, 'opacity-70')}>{meta.label}</p>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ToolSkeletonCard;
